import { useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  FileText, Sparkles, Loader2, Download, Copy, Check,
  BarChart3, AlertTriangle, Shield, TrendingUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const focusOptionDefs = [
  { id: "general", nameKey: "exec.general", descKey: "exec.general_desc", icon: BarChart3 },
  { id: "risk", nameKey: "exec.risk", descKey: "exec.risk_desc", icon: AlertTriangle },
  { id: "compliance", nameKey: "exec.compliance", descKey: "exec.compliance_desc", icon: Shield },
  { id: "incidents", nameKey: "exec.incidents", descKey: "exec.incidents_desc", icon: AlertTriangle },
  { id: "improvement", nameKey: "exec.improvement", descKey: "exec.improvement_desc", icon: TrendingUp }
];

const ExecutiveReport = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [focus, setFocus] = useState("general");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/executive-report/generate?focus=${focus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
      toast.success(t("exec.report_generated"));
    } catch (error) {
      toast.error(t("exec.generation_failed"));
    } finally {
      setGenerating(false);
    }
  };

  const copyReport = () => {
    if (result?.report) {
      navigator.clipboard.writeText(result.report);
      setCopied(true);
      toast.success(t("copied"));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadReport = () => {
    if (!result?.report) return;
    const blob = new Blob([result.report], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Executive_Report_${focus}_${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6" data-testid="exec-report-page">
      <PageHeader
        title={t("exec.title")}
        description={t("exec.subtitle")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Config */}
        <Card className="border-border" data-testid="report-config-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> {t("exec.focus")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {focusOptionDefs.map(opt => (
              <div key={opt.id}
                className={`p-3 rounded-lg cursor-pointer transition-all border ${focus === opt.id ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/50'}`}
                onClick={() => setFocus(opt.id)}
                data-testid={`focus-${opt.id}`}
              >
                <div className="flex items-center gap-2">
                  <opt.icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{t(opt.nameKey)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t(opt.descKey)}</p>
              </div>
            ))}
            <Button onClick={generate} disabled={generating} className="w-full mt-2" data-testid="generate-exec-report-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? t("generating") : t("exec.generate_report")}
            </Button>
          </CardContent>
        </Card>

        {/* Result */}
        <div className="lg:col-span-3">
          <Card className="border-border h-full" data-testid="exec-report-result">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t("exec.board_report")}</CardTitle>
                {result && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyReport}>
                      {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copied ? t("copied") : t("copy")}
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadReport}>
                      <Download className="w-4 h-4 mr-1" /> {t("download")}
                    </Button>
                  </div>
                )}
              </div>
              {result?.metrics_snapshot && (
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant="outline">{t("exec.compliance_label")}: {result.metrics_snapshot.overall_compliance}%</Badge>
                  <Badge variant="outline">{t("exec.health_label")}: {result.metrics_snapshot.health_score} ({result.metrics_snapshot.health_grade})</Badge>
                  {result.metrics_snapshot.critical_risks > 0 && (
                    <Badge variant="destructive">{result.metrics_snapshot.critical_risks} {t("exec.critical_risks")}</Badge>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {generating ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                  <p className="text-muted-foreground">{t("exec.preparing")}</p>
                </div>
              ) : result ? (
                <ScrollArea className="h-[calc(100vh-20rem)]">
                  <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap leading-relaxed">
                    {result.report}
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <FileText className="w-12 h-12 text-muted-foreground mb-4 opacity-50" />
                  <p className="text-muted-foreground">{t("exec.select_focus")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("exec.ai_translates")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveReport;
